/**
 * Leitura do parâmetro `box` vindo das landing pages de rota do front-gbs
 * (`/[embarcalog]/routes/[origin]/[destination]`).
 *
 * Contrato: AES-GCM 256, chave = SHA-256 de `NEXT_PUBLIC_BOX_PARAM_SECRET`,
 * transportado como `base64url(iv ‖ ciphertext)`. O emissor é
 * `src/utils/boxParamCrypto.ts` no front-gbs.
 *
 * O segredo precisa ser IDÊNTICO nos dois projetos — divergência faz a
 * decifragem devolver `null` silenciosamente (o volume simplesmente não é
 * pré-preenchido).
 *
 * Modelo de ameaça: a cifragem acontece no browser, então o segredo vai no
 * bundle e é extraível — isto dá OPACIDADE da URL, não autenticidade. Aceito de
 * propósito: um payload forjado só pré-preenche um formulário que já fixa os
 * valores nos limites máximos. Por isso `clampBoxPayload` abaixo é obrigatório
 * antes de qualquer uso.
 */

export interface BoxParamPayload {
    lengthCm: number;
    widthCm: number;
    heightCm: number;
    weightKg: number;
    unitValueBRL: number;
    quantity: number;
}

const SECRET = process.env.NEXT_PUBLIC_BOX_PARAM_SECRET ?? "";
const IV_BYTES = 12;

const fromBase64Url = (value: string): Uint8Array => {
    const padded = value
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(value.length / 4) * 4, "=");
    return Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
};

/**
 * Decifra o token. Devolve `null` para qualquer entrada inválida — token
 * adulterado, segredo divergente, lixo ou Web Crypto indisponível. Nunca lança:
 * a tela precisa abrir mesmo sem o volume pré-preenchido.
 */
export const decryptBoxParam = async (
    value: string | null | undefined
): Promise<BoxParamPayload | null> => {
    if (!value || !SECRET) return null;
    if (typeof crypto === "undefined" || !crypto.subtle) return null;

    try {
        const packed = fromBase64Url(value);
        if (packed.length <= IV_BYTES) return null;

        const keyMaterial = await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(SECRET)
        );
        const key = await crypto.subtle.importKey(
            "raw",
            keyMaterial,
            { name: "AES-GCM" },
            false,
            ["decrypt"]
        );

        const plaintext = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: packed.slice(0, IV_BYTES) },
            key,
            packed.slice(IV_BYTES)
        );

        const parsed = JSON.parse(new TextDecoder().decode(plaintext));
        if (!parsed || typeof parsed !== "object") return null;
        return parsed as BoxParamPayload;
    } catch {
        // Tag de autenticação do GCM reprova adulteração; JSON inválido cai aqui
        // também. Nos dois casos o comportamento correto é "sem box".
        return null;
    }
};

const clampNumber = (value: unknown, max: number): number => {
    const num = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.min(num, max);
};

/**
 * Sujeita o payload às MESMAS restrições da digitação manual, como exige o
 * modelo de ameaça: nada entra no formulário por um caminho que o teclado não
 * teria. Campo inválido ou ausente vira `0`, que o formulário já trata como
 * "não preenchido".
 */
export const clampBoxPayload = (
    box: BoxParamPayload,
    dimMaxes: { lengthCm: number; widthCm: number; heightCm: number },
    maxWeightKg: number
): BoxParamPayload => ({
    lengthCm: clampNumber(box.lengthCm, dimMaxes.lengthCm),
    widthCm: clampNumber(box.widthCm, dimMaxes.widthCm),
    heightCm: clampNumber(box.heightCm, dimMaxes.heightCm),
    weightKg: clampNumber(box.weightKg, maxWeightKg),
    unitValueBRL: clampNumber(box.unitValueBRL, Number.MAX_SAFE_INTEGER),
    // Esta tela sempre cota quantidade 1 (ver a nota em readBasePackages).
    quantity: 1,
});
